/**
 * MemberNotificationsPage — the member portal's full notifications list.
 *
 * Reuses the shared, user-scoped /api/notifications system (members log in as
 * users) through member auth (memberApiFetch) — never the staff apiClient.
 * Supports All / Unread filtering, per-item mark-read, mark-all-read, and
 * navigating a notification's `link` when it maps to a member-portal route.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Button } from '@/components/ui/button';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  isAuthenticated,
  getMemberNotifications,
  markMemberNotificationRead,
  markAllMemberNotificationsRead,
  mapNotificationLinkToMemberRoute,
  type MemberNotification,
} from '@/lib/memberAuth';

type Filter = 'all' | 'unread';

export default function MemberNotificationsPage() {
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const [items, setItems] = useState<MemberNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (f: Filter) => {
    setLoading(true);
    setError('');
    try {
      const data = await getMemberNotifications({
        page_size: 50,
        unread_only: f === 'unread',
      });
      setItems(data.notifications || []);
    } catch {
      setError(t('notifications.loadError', 'Could not load your notifications. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }
    load(filter);
  }, [filter, load, navigate]);

  const unreadCount = items.filter((n) => !n.read).length;

  const handleMarkRead = async (id: number) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await markMemberNotificationRead(id);
    } catch {
      load(filter); // reconcile on failure
    }
  };

  const handleMarkAllRead = async () => {
    setBusy(true);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await markAllMemberNotificationsRead();
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
    load(filter);
  };

  const handleClick = async (n: MemberNotification) => {
    if (!n.read) await handleMarkRead(n.id);
    const route = mapNotificationLinkToMemberRoute(n.link);
    if (route) navigate(route);
  };

  const fmtWhen = (iso: string) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={t('dashboard.memberPortal', 'Member Portal')}
        title={t('notifications.title', 'Notifications')}
        description={t('notifications.pageSubtitle', 'Updates about your programs, messages, and requests')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={busy || unreadCount === 0}
          >
            <CheckCheck className="mr-1.5 h-4 w-4" />
            {t('ui.mark_all_as_read', 'Mark all as read')}
          </Button>
        }
        tabs={[
          { key: 'all', label: t('notifications.all', 'All') },
          { key: 'unread', label: t('notifications.unread', 'Unread'), count: unreadCount || undefined },
        ]}
        activeTab={filter}
        onTabChange={(k) => setFilter(k as Filter)}
      />

      <PageContainer>
        {loading ? (
          <PageState variant="loading" title={t('common.loading', 'Loading...')} />
        ) : error ? (
          <PageState
            variant="error"
            title={t('common.error', 'Something went wrong')}
            message={error}
            action={
              <Button variant="outline" size="sm" onClick={() => load(filter)}>
                {t('buttons.retry', 'Retry')}
              </Button>
            }
          />
        ) : items.length === 0 ? (
          <PageState
            variant="empty"
            icon={<Bell className="h-6 w-6 text-ink-300" />}
            title={
              filter === 'unread'
                ? t('notifications.noUnread', 'No unread notifications')
                : t('notifications.noNewNotifications', 'No notifications yet')
            }
            message={t('notifications.allCaughtUp', "You're all caught up!")}
          />
        ) : (
          <ul className="overflow-hidden rounded-[10px] border border-v2border bg-surface shadow-v2-1">
            {items.map((n, i) => {
              const linkRoute = mapNotificationLinkToMemberRoute(n.link);
              return (
                <li
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-paper-2 ${
                    i > 0 ? 'border-t border-v2border' : ''
                  } ${!n.read ? 'bg-teal-50/40 dark:bg-teal-900/10' : ''}`}
                >
                  <div className="mt-1 flex-shrink-0">
                    {!n.read ? (
                      <span className="block h-2 w-2 rounded-full bg-teal-500" />
                    ) : (
                      <span className="block h-2 w-2 rounded-full bg-transparent" />
                    )}
                  </div>
                  <button
                    onClick={() => handleClick(n)}
                    className="min-w-0 flex-1 text-left"
                    disabled={!linkRoute && !n.link}
                  >
                    <div className="flex items-center gap-2">
                      <p className="truncate text-[14px] font-semibold text-ink">{n.title}</p>
                      {n.priority === 'urgent' && (
                        <span className="flex-shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                          {t('ui.urgent', 'Urgent')}
                        </span>
                      )}
                      {linkRoute && <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-ink-400" />}
                    </div>
                    <p className="mt-0.5 text-[13px] text-ink-600">{n.message}</p>
                    <p className="mt-1 text-[11px] text-ink-400">{fmtWhen(n.created_at)}</p>
                  </button>
                  {!n.read && (
                    <button
                      onClick={() => handleMarkRead(n.id)}
                      title={t('notifications.markAsRead', 'Mark as read')}
                      className="mt-0.5 flex flex-shrink-0 items-center gap-1 rounded-[var(--v2-r-sm)] px-2 py-1 text-[12px] font-medium text-ink-500 hover:bg-paper hover:text-teal-600"
                    >
                      <Check className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{t('notifications.markAsRead', 'Mark as read')}</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </PageContainer>
    </>
  );
}
